import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ArrowRight, HousePlus } from 'lucide-react';
import { RetrofitSurveyData, getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import PhotoCapture from '../PhotoCapture';
import { PhotoData } from '../../services/CameraService';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import PhotoGalleryModal from '../PhotoGalleryModal';
import { analyzeSectionFileSize, logFileSizeAnalysis } from '../../utils/fileSizeAnalysis';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

// --- INTERFACES ---
export interface ExtensionsFormProps {
  onNavigateNext: () => void;
  onNavigateBack: () => void;
  floorPlanId?: string;
  surveyData?: RetrofitSurveyData;
  surveyId?: string;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  onSaveImmediately?: () => Promise<string>;
}

interface DisplayablePhotoData extends PhotoData {
  displayId: string;
  dataUrl: string;      // Compressed base64 - always available for offline
  fileName?: string;    // Supabase storage path
  url?: string;         // Supabase CDN URL after upload
  uploading?: boolean;  // Upload status
  error?: string;       // Upload error message
  note?: string;        // Photo notes/description
  caption?: string;     // Photo caption
  timestamp?: string;   // Photo timestamp
}

interface ExtensionPhotos {
  wallEvidence: DisplayablePhotoData[];
  wallThickness: DisplayablePhotoData[];
  wallInsulationImages: DisplayablePhotoData[];
  roofTypeEvidence: DisplayablePhotoData[];
  externalElevations: DisplayablePhotoData[];
}

interface ConservatoryPhotos {
  externalElevationImages: DisplayablePhotoData[];
  internalImages: DisplayablePhotoData[];
}

interface ExtensionData {
  id: string;
  type: string;
  age: string;
  condition: string;
  heatingProvision: string;
  notes: string;
  area?: string; // Area in m² from floor plan calculations
  hlp?: string; // Heat Loss Perimeter in m from floor plan
  pw?: string; // Party Wall length in m from floor plan
  height?: string; // Height in m (manual input or from floor plan)
  numberOfStoreys?: string;
  buildingAge?: string;
  wallConstructionType?: string;
  isWallInsulated?: boolean | null;
  wallThicknessMeasurement?: string; // Wall thickness in mm
  roofType?: string;
  photos?: ExtensionPhotos; // Optional for compatibility
}

// --- OPTIONS ---
const numberOptions = (max: number, labelPrefix = "Select") => {
  const options = [{ value: "", label: `${labelPrefix} value` }];
  for (let i = 1; i <= max; i++) {
    options.push({ value: i.toString(), label: i.toString() });
  }
  return options;
};

const buildingAgeOptions = [
    { value: "", label: "Select Building Age" },
    { value: "before-1900", label: "A- before 1900" },
    { value: "1900-1929", label: "B- 1900-1929" },
    { value: "1930-1949", label: "C- 1930-1949" },
    { value: "1950-1966", label: "D- 1950-1966" },
    { value: "1967-1975", label: "E- 1967-1975" },
    { value: "1976-1982", label: "F- 1976-1982" },
    { value: "1983-1990", label: "G- 1983-1990" },
    { value: "1991-1995", label: "H- 1991-1995" },
    { value: "1996-2002", label: "I- 1996-2002" },
    { value: "2003-2006", label: "J- 2003-2006" },
    { value: "2007-2011", label: "K- 2007-2011" },
    { value: "2012-2022", label: "L- 2012-2022" },
    { value: "2023-onwards", label: "M- 2023 onwards" },
];

const wallConstructionTypeOptions = [
  { value: "", label: "Select Wall Type" },
  { value: "stone_granite_whinstone", label: "Stone: granite or whinstone" },
  { value: "stone_sandstone_limestone", label: "Stone: sandstone or limestone" },
  { value: "solid_brick", label: "Solid Brick" },
  { value: "cob", label: "Cob" },
  { value: "cavity", label: "Cavity" },
  { value: "timber_frame", label: "Timber Frame" },
  { value: "system_build", label: "System Build" },
  { value: "curtain_wall", label: "Curtain Wall" },
];

const roofTypeOptions = [
  { value: "pitched", label: "Pitched" },
  { value: "flat", label: "Flat" },
  { value: "thatched", label: "Thatched" },
  { value: "sloping", label: "Sloping" },
  { value: "dwelling_above", label: "Dwelling Above" },
];

const initialExtensionPhotos: ExtensionPhotos = {
    wallEvidence: [],
    wallThickness: [],
    wallInsulationImages: [],
    roofTypeEvidence: [],
    externalElevations: [],
};

const initialExtensionData: Omit<ExtensionData, 'id'> = {
    type: '',
    age: '',
    condition: '',
    heatingProvision: '',
    notes: '',
    photos: { ...initialExtensionPhotos }
};

// --- COMPONENT ---
const ExtensionsForm: React.FC<ExtensionsFormProps> = ({
  onNavigateNext,
  onNavigateBack,
  floorPlanId,
  surveyData,
  surveyId,
  onDataUpdate,
}) => {
  const [numberOfExtensions, setNumberOfExtensions] = useState(surveyData?.extensions?.numberOfExtensions || '');
  const [extensions, setExtensions] = useState<ExtensionData[]>(surveyData?.extensions?.existingExtensions || []);
  const [viewingGallery, setViewingGallery] = useState<{ extIndex: number; photoType: keyof ExtensionPhotos } | null>(null);
  const [viewingConservatoryGallery, setViewingConservatoryGallery] = useState<keyof ConservatoryPhotos | null>(null);
  
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(
    surveyId || surveyData?.id || floorPlanId || undefined,
    propertyName
  );

  const [hasConservatory, setHasConservatory] = useState<boolean>(surveyData?.extensions?.hasConservatory ?? false);
  const [conservatoryPhotos, setConservatoryPhotos] = useState<ConservatoryPhotos>({
    externalElevationImages: [],
    internalImages: [],
  });

  // Sync surveyData prop changes back to form state
  useEffect(() => {
    if (surveyData?.extensions) {
      const savedExtensions = surveyData.extensions.existingExtensions || [];
      
      // Sync all extensions from surveyData (including photos and floor plan metrics)
      if (savedExtensions.length > 0) {
        // Check if we need to populate HLP and PW from floor plan data
        const siteNotesFloors = surveyData?.floor_plan?.linkedFloorPlanData?.siteNotesData?.floors || [];
        const extensionFloors = siteNotesFloors.filter((f: any) => f.buildingType === 'extension');
        
        console.log('📐 [Extensions] Floor plan data:', {
          totalSiteNotesFloors: siteNotesFloors.length,
          extensionFloors: extensionFloors.length,
          extensionFloorsData: extensionFloors.map((f: any) => ({
            id: f.id,
            floorArea: f.floorArea,
            hlp: f.hlp,
            pw: f.pw
          }))
        });
        
        // Map extensions to include HLP and PW from floor plan if missing
        const enhancedExtensions = savedExtensions.map((ext: any, index) => {
          // If extension already has hlp and pw, keep them
          if (ext.hlp && ext.pw) return ext as ExtensionData;
          
          // Otherwise, try to get from floor plan data
          const matchingFloor = extensionFloors[index]; // Match by index for now
          if (matchingFloor) {
            return {
              ...ext,
              hlp: ext.hlp || matchingFloor.hlp?.replace(' m', ''),
              pw: ext.pw || matchingFloor.pw?.replace(' m', '')
            } as ExtensionData;
          }
          
          return ext as ExtensionData;
        });
        
        console.log('📐 [Extensions] Enhanced extensions with HLP/PW:', enhancedExtensions.map((ext, i) => ({
          index: i,
          id: ext.id,
          type: ext.type,
          area: ext.area,
          hlp: ext.hlp,
          pw: ext.pw
        })));
        
        setExtensions(enhancedExtensions);
      }
      
      // Sync numberOfExtensions if surveyData has a value and it's higher than current
      if (surveyData.extensions.numberOfExtensions) {
        const savedCount = parseInt(surveyData.extensions.numberOfExtensions);
        const currentCount = parseInt(numberOfExtensions || '0');
        
        if (savedCount > currentCount || !numberOfExtensions) {
          setNumberOfExtensions(surveyData.extensions.numberOfExtensions);
        }
      }
      
      if (surveyData.extensions.hasConservatory !== undefined && surveyData.extensions.hasConservatory !== null) {
        setHasConservatory(surveyData.extensions.hasConservatory ?? false);
      }
      
      // Sync conservatory photos from surveyData
      if (surveyData.extensions.conservatoryPhotos) {
        setConservatoryPhotos(surveyData.extensions.conservatoryPhotos);
      }
    }
  }, [surveyData]);

  useEffect(() => {
    const num = parseInt(numberOfExtensions, 10) || 0;
    
    setExtensions(prev => {
      // Keep existing extensions and add empty slots for additional ones
      const newExtensions = Array.from({ length: num }, (_, i) => {
        // If we already have an extension at this index, keep it
        if (prev[i]) {
          return prev[i];
        }
        // Otherwise create a new empty extension slot
        return { 
          id: `extension-${i + 1}-${Date.now()}`,
          ...initialExtensionData,
          photos: { ...initialExtensionPhotos }
        };
      });
      
      return newExtensions.slice(0, num);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfExtensions]);

  // 📊 FILE SIZE ANALYSIS: Photo fields for extensions section
  const EXTENSIONS_PHOTO_FIELDS = [
    'existingExtensions', // This contains nested photos for each extension
    'conservatoryPhotos'
  ];

  // 📊 FILE SIZE ANALYSIS: Analyze extensions section
  const analyzeExtensionsFileSize = useCallback(() => {
    const result = analyzeSectionFileSize(
      'extensions',
      surveyData?.extensions,
      EXTENSIONS_PHOTO_FIELDS
    );
    logFileSizeAnalysis('extensions', result, 'cleanupExtensionsBase64');
  }, [surveyData?.extensions]);

  // Run analysis on data load
  useEffect(() => {
    if (surveyData?.extensions) {
      const timer = setTimeout(() => analyzeExtensionsFileSize(), 1000);
      return () => clearTimeout(timer);
    }
  }, [surveyData?.extensions, analyzeExtensionsFileSize]);

  // Expose to window for manual testing
  useEffect(() => {
    (window as any).analyzeExtensions = analyzeExtensionsFileSize;
    return () => {
      delete (window as any).analyzeExtensions;
    };
  }, [analyzeExtensionsFileSize]);

  // 🧹 CLEANUP: Strip base64 from photos that already have CDN URLs
  const cleanupBase64FromCDNPhotos = useCallback(() => {
    console.log('🧹 [BASE64 CLEANUP - EXTENSIONS] Checking for photos with CDN URLs that still have base64...');
    
    let cleanupNeeded = false;
    
    // Clean extension photos
    const cleanedExtensions = extensions.map(ext => {
      if (!ext.photos) return ext;
      
      const cleanedPhotos: ExtensionPhotos = {
        wallEvidence: [],
        wallThickness: [],
        wallInsulationImages: [],
        roofTypeEvidence: [],
        externalElevations: []
      };
      
      (Object.keys(ext.photos) as Array<keyof ExtensionPhotos>).forEach(photoType => {
        const photoArray = ext.photos![photoType];
        cleanedPhotos[photoType] = photoArray.map((photo: any) => {
          if (photo && photo.url && photo.url.startsWith('http') && (photo.dataUrl || photo.base64String)) {
            console.log(`🧹 [BASE64 CLEANUP - EXTENSIONS] Stripping base64 from extension photo ${photo.displayId}`);
            cleanupNeeded = true;
            return {
              displayId: photo.displayId,
              format: photo.format,
              fileName: photo.fileName,
              url: photo.url,
              uploading: false,
              error: undefined
            };
          }
          return photo;
        });
      });
      
      return { ...ext, photos: cleanedPhotos };
    });
    
    // Clean conservatory photos
    const cleanedConservatoryPhotos: ConservatoryPhotos = {
      externalElevationImages: [],
      internalImages: []
    };
    
    (Object.keys(conservatoryPhotos) as Array<keyof ConservatoryPhotos>).forEach(photoType => {
      const photoArray = conservatoryPhotos[photoType];
      cleanedConservatoryPhotos[photoType] = photoArray.map((photo: any) => {
        if (photo && photo.url && photo.url.startsWith('http') && (photo.dataUrl || photo.base64String)) {
          console.log(`🧹 [BASE64 CLEANUP - EXTENSIONS] Stripping base64 from conservatory photo ${photo.displayId}`);
          cleanupNeeded = true;
          return {
            displayId: photo.displayId,
            format: photo.format,
            fileName: photo.fileName,
            url: photo.url,
            uploading: false,
            error: undefined
          };
        }
        return photo;
      });
    });
    
    if (cleanupNeeded) {
      console.log('🧹 [BASE64 CLEANUP - EXTENSIONS] ✅ Cleaned photos, updating state and saving...');
      setExtensions(cleanedExtensions);
      setConservatoryPhotos(cleanedConservatoryPhotos);
      
      // Save cleaned data to database
      if (onDataUpdate) {
        setTimeout(() => {
          console.log('🧹 [BASE64 CLEANUP - EXTENSIONS] 💾 Saving cleaned photos to database');
          onDataUpdate(prevData => ({
            ...prevData,
            extensions: {
              ...prevData.extensions,
              numberOfExtensions,
              existingExtensions: cleanedExtensions,
              hasConservatory,
              conservatoryPhotos: cleanedConservatoryPhotos
            }
          }), 'extensions');
        }, 100);
      }
    } else {
      console.log('🧹 [BASE64 CLEANUP - EXTENSIONS] No cleanup needed, all photos are clean');
    }
  }, [extensions, conservatoryPhotos, numberOfExtensions, hasConservatory, onDataUpdate]);

  // Expose cleanup function to window
  useEffect(() => {
    (window as any).cleanupExtensionsBase64 = cleanupBase64FromCDNPhotos;
    return () => {
      delete (window as any).cleanupExtensionsBase64;
    };
  }, [cleanupBase64FromCDNPhotos]);

  // 🧹 AUTO-CLEANUP: Automatically clean base64 from photos with CDN URLs on page load
  useEffect(() => {
    if (!extensions.length && !conservatoryPhotos.externalElevationImages.length && !conservatoryPhotos.internalImages.length) return;
    
    // Check if any photos have CDN URLs but still have base64
    const extNeedsCleanup = extensions.some(ext => {
      if (!ext.photos) return false;
      return Object.values(ext.photos).some(photoArray => 
        photoArray.some((p: any) => p && p.url && p.url.startsWith('http') && (p.dataUrl || p.base64String))
      );
    });
    
    const conservatoryNeedsCleanup = Object.values(conservatoryPhotos).some(photoArray =>
      photoArray.some((p: any) => p && p.url && p.url.startsWith('http') && (p.dataUrl || p.base64String))
    );
    
    if (extNeedsCleanup || conservatoryNeedsCleanup) {
      console.log('🧹 [AUTO-CLEANUP - EXTENSIONS] Detected photos with CDN + base64, running automatic cleanup in 3 seconds...');
      const timer = setTimeout(() => {
        cleanupBase64FromCDNPhotos();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [extensions, conservatoryPhotos, cleanupBase64FromCDNPhotos]);

  const handleConservatoryBooleanChange = (value: boolean) => {
    setHasConservatory(value);
    if (!value) {
      // Clear photos if user selects "No"
      setConservatoryPhotos({ externalElevationImages: [], internalImages: [] });
    }
    
    // Trigger autosave via parent callback
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        extensions: {
          ...prev.extensions,
          hasConservatory: value
        }
      }), 'extensions');
    }
  };

  const handleExtensionChange = <K extends keyof Omit<ExtensionData, 'id' | 'photos'>>(index: number, field: K, value: ExtensionData[K]) => {
    const newExtensions = [...extensions];
    if(newExtensions[index]) {
        newExtensions[index] = { ...newExtensions[index], [field]: value };
        setExtensions(newExtensions);
        
        // Trigger autosave via parent callback
        if (onDataUpdate) {
          onDataUpdate(prev => ({
            ...prev,
            extensions: {
              ...prev.extensions,
              existingExtensions: newExtensions
            }
          }), 'extensions');
        }
    }
  };
  
  const handlePhotosCaptured = (extIndex: number, photoType: keyof ExtensionPhotos, capturedPhotos: PhotoData[]) => {
      const newImages: DisplayablePhotoData[] = capturedPhotos.map((p, idx) => {
        const displayId = `ext-${extIndex}-${photoType}-${Date.now()}-${idx}`;
        const dataUrl = `data:image/${p.format};base64,${p.base64String}`;
        return {
          ...p,
          displayId,
          dataUrl,
          uploading: true,
          fileName: undefined,
          url: undefined,
          error: undefined
        };
      });

      // Queue photos for background upload
      newImages.forEach(photo => {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          'extensions',
          `ext-${extIndex}-${photoType}`,
          (url: string) => {
            setExtensions(current => {
              const updated = [...current];
              if (updated[extIndex] && updated[extIndex].photos) {
                updated[extIndex] = {
                  ...updated[extIndex],
                  photos: {
                    ...updated[extIndex].photos!,
                    [photoType]: (updated[extIndex].photos![photoType] || []).map(p => 
                      p.displayId === photo.displayId 
                        ? { ...p, url, uploading: false, error: undefined }
                        : p
                    )
                  }
                };
              }
              
              // Persist upload success to surveyData
              if (onDataUpdate) {
                onDataUpdate(prevData => ({
                  ...prevData,
                  extensions: {
                    ...prevData.extensions,
                    existingExtensions: updated
                  }
                }), 'extensions');
              }
              
              return updated;
            });
          },
          (error: string) => {
            setExtensions(current => {
              const updated = [...current];
              if (updated[extIndex] && updated[extIndex].photos) {
                updated[extIndex] = {
                  ...updated[extIndex],
                  photos: {
                    ...updated[extIndex].photos!,
                    [photoType]: (updated[extIndex].photos![photoType] || []).map(p => 
                      p.displayId === photo.displayId 
                        ? { ...p, uploading: false, error }
                        : p
                    )
                  }
                };
              }
              
              // Persist upload error to surveyData
              if (onDataUpdate) {
                onDataUpdate(prevData => ({
                  ...prevData,
                  extensions: {
                    ...prevData.extensions,
                    existingExtensions: updated
                  }
                }), 'extensions');
              }
              
              return updated;
            });
          }
        );
      });

      setExtensions(prev => {
          const updated = [...prev];
          if (updated[extIndex]) {
            const existingPhotos = updated[extIndex].photos || { ...initialExtensionPhotos };
            updated[extIndex] = {
                ...updated[extIndex],
                photos: { ...existingPhotos, [photoType]: [...(existingPhotos[photoType] || []), ...newImages] }
            };
          }
          
          // Persist photos to surveyData immediately
          if (onDataUpdate) {
            onDataUpdate(prevData => ({
              ...prevData,
              extensions: {
                ...prevData.extensions,
                existingExtensions: updated
              }
            }), 'extensions');
          }
          
          return updated;
      });
  };

  const handleConservatoryPhotosCaptured = (photoType: keyof ConservatoryPhotos, capturedPhotos: PhotoData[]) => {
    const newImages: DisplayablePhotoData[] = capturedPhotos.map((p, idx) => {
      const displayId = `${photoType}_${Date.now()}_${idx}`;
      const dataUrl = `data:image/${p.format};base64,${p.base64String}`;
      return {
        ...p,
        displayId,
        dataUrl,
        uploading: true,
        fileName: undefined,
        url: undefined,
        error: undefined
      };
    });

    // Queue photos for background upload
    newImages.forEach(photo => {
      queuePhotoUpload(
        photo.displayId,
        photo.dataUrl,
        'conservatory',
        photoType,
        (url: string) => {
          setConservatoryPhotos(current => {
            const updated = {
              ...current,
              [photoType]: current[photoType].map(p => 
                p.displayId === photo.displayId 
                  ? { ...p, url, uploading: false, error: undefined }
                  : p
              )
            };
            
            // Persist upload success to surveyData
            if (onDataUpdate) {
              onDataUpdate(prevData => ({
                ...prevData,
                extensions: {
                  ...prevData.extensions,
                  conservatoryPhotos: updated
                }
              }), 'extensions');
            }
            
            return updated;
          });
        },
        (error: string) => {
          setConservatoryPhotos(current => {
            const updated = {
              ...current,
              [photoType]: current[photoType].map(p => 
                p.displayId === photo.displayId 
                  ? { ...p, uploading: false, error }
                  : p
              )
            };
            
            // Persist upload error to surveyData
            if (onDataUpdate) {
              onDataUpdate(prevData => ({
                ...prevData,
                extensions: {
                  ...prevData.extensions,
                  conservatoryPhotos: updated
                }
              }), 'extensions');
            }
            
            return updated;
          });
        }
      );
    });

    setConservatoryPhotos(prev => {
      const updated = {
        ...prev,
        [photoType]: [...prev[photoType], ...newImages]
      };
      
      // Persist photos to surveyData immediately
      if (onDataUpdate) {
        onDataUpdate(prevData => ({
          ...prevData,
          extensions: {
            ...prevData.extensions,
            conservatoryPhotos: updated
          }
        }), 'extensions');
      }
      
      return updated;
    });
  };

  const removePhoto = async (extIndex: number, photoType: keyof ExtensionPhotos, displayIdToRemove: string) => {
    console.log(`[Extensions] 🗑️ Removing photo from extension ${extIndex}, ${photoType}, displayId: ${displayIdToRemove}`);
    
    const newExtensions = [...extensions];
    if (newExtensions[extIndex] && newExtensions[extIndex].photos) {
        const currentPhotos = newExtensions[extIndex].photos![photoType] || [];
        const photoToDelete = currentPhotos.find(p => p.displayId === displayIdToRemove);
        
        // Delete from Supabase Storage
        if (photoToDelete) {
          let filePath = (photoToDelete as any).fileName;
          if (!filePath && photoToDelete.url?.includes('/storage/v1/object/public/')) {
            filePath = photoToDelete.url.split('/storage/v1/object/public/')[1]?.split('/').slice(1).join('/');
          }
          if (filePath) {
            try {
              await deletePhotoFromSupabase(filePath, 'retrofit-survey-images');
              console.log(`[Extensions] ✅ Deleted from storage: ${filePath}`);
            } catch (error) {
              console.warn(`[Extensions] ⚠️ Failed to delete from storage:`, error);
            }
          }
        }
        
        newExtensions[extIndex].photos![photoType] = currentPhotos.filter(p => p.displayId !== displayIdToRemove);
        setExtensions(newExtensions);
        
        // Persist photo removal to surveyData
        if (onDataUpdate) {
          onDataUpdate(prev => ({
            ...prev,
            extensions: {
              ...prev.extensions,
              existingExtensions: newExtensions
            }
          }), 'extensions');
        }
    }
  };

  const removeConservatoryPhoto = async (photoType: keyof ConservatoryPhotos, displayIdToRemove: string) => {
    console.log(`[Conservatory] 🗑️ Removing photo from ${photoType}, displayId: ${displayIdToRemove}`);
    
    const currentPhotos = conservatoryPhotos[photoType] || [];
    const photoToDelete = currentPhotos.find(p => p.displayId === displayIdToRemove);
    
    // Delete from Supabase Storage
    if (photoToDelete) {
      let filePath = (photoToDelete as any).fileName;
      if (!filePath && photoToDelete.url?.includes('/storage/v1/object/public/')) {
        filePath = photoToDelete.url.split('/storage/v1/object/public/')[1]?.split('/').slice(1).join('/');
      }
      if (filePath) {
        try {
          await deletePhotoFromSupabase(filePath, 'retrofit-survey-images');
          console.log(`[Conservatory] ✅ Deleted from storage: ${filePath}`);
        } catch (error) {
          console.warn(`[Conservatory] ⚠️ Failed to delete from storage:`, error);
        }
      }
    }
    
    setConservatoryPhotos(prev => {
      const updated = {
        ...prev,
        [photoType]: prev[photoType].filter(p => p.displayId !== displayIdToRemove)
      };
      
      // Persist photo removal to surveyData
      if (onDataUpdate) {
        onDataUpdate(prevData => ({
          ...prevData,
          extensions: {
            ...prevData.extensions,
            conservatoryPhotos: updated
          }
        }), 'extensions');
      }
      
      return updated;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Extensions Data:', { floorPlanId, numberOfExtensions, extensions, hasConservatory, conservatoryPhotos });
    onNavigateNext();
  };
  
  const renderMultiplePhotoThumbnails = (photoDatas: DisplayablePhotoData[], onClick: () => void, altTextPrefix: string) => {
    if (!photoDatas || photoDatas.length === 0) return <p className="text-xs text-gray-500 mt-1">No photos added yet.</p>;
    
    // Show only first photo with count
    const firstPhoto = photoDatas[0];
    const imageUrl = firstPhoto.url || firstPhoto.dataUrl;
    if (!imageUrl) return <p className="text-xs text-gray-500 mt-1">No photos added yet.</p>;
    
    return (
      <div className="mt-2">
        <div 
          className="w-24 h-24 relative cursor-pointer group flex-shrink-0"
          onClick={onClick}
        >
          <img 
            src={imageUrl} 
            alt={`${altTextPrefix}`} 
            className="w-full h-full object-cover rounded-lg border-2 border-yellow-300 shadow-sm hover:border-yellow-400 transition-colors" 
          />
          {photoDatas.length > 1 && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-white text-sm font-semibold">{photoDatas.length} photos</span>
            </div>
          )}
          <div className="absolute bottom-1 right-1 bg-yellow-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
            {photoDatas.length}
          </div>
        </div>
      </div>
    );
  };

  const commonSelectClassName = "w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400";
  const fieldWrapperClass = "space-y-2";
  const labelClass = "text-sm font-medium text-gray-700 flex items-center";
  const requiredMarker = <span className="text-red-500 mr-1">*</span>;
  const photoButtonClass = "w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 transition-all duration-200";
  const photoCaptureContainerClass = "p-4 border border-gray-200 rounded-lg bg-gray-50/30 space-y-2";
  const booleanButtonClass = (isSelected: boolean | null, actualValue: boolean) =>
    `flex-1 py-3 rounded-lg border-2 ${isSelected === actualValue ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`;


  return (
    <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-xl shadow-lg border border-yellow-200 min-h-[calc(100vh-12rem)] flex flex-col">
       <h2 className="text-xl sm:text-2xl font-bold text-gray-900 border-b-2 border-gray-200 pb-4 mb-6 flex items-center gap-3">
        <HousePlus />
        Extensions & Conservatory
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6 flex-1 flex flex-col">
        <div className="flex-1 space-y-6">
          <fieldset className="border border-gray-300 p-4 rounded-lg">
            <legend className="text-md font-semibold text-gray-700 px-2">Conservatory Details</legend>
            <div className="space-y-4 mt-2">
              <div className={fieldWrapperClass}>
                <label className={labelClass}>{requiredMarker}Is there a conservatory?</label>
                <div className="flex space-x-4 mt-2">
                    <button type="button" onClick={() => handleConservatoryBooleanChange(true)} className={booleanButtonClass(hasConservatory, true)}>Yes</button>
                    <button type="button" onClick={() => handleConservatoryBooleanChange(false)} className={booleanButtonClass(hasConservatory, false)}>No</button>
                </div>
              </div>
              
              {hasConservatory && (
                <div className="space-y-6">
                  <div className={photoCaptureContainerClass}>
                    <label className={labelClass}>{requiredMarker}All External Elevation Images</label>
                    <PhotoCapture
                      onPhotosCaptured={(p) => handleConservatoryPhotosCaptured('externalElevationImages', p)}
                      maxPhotos={999}
                      buttonLabel="Capture External Images"
                      buttonClass={photoButtonClass}
                    />
                    {renderMultiplePhotoThumbnails(conservatoryPhotos.externalElevationImages, () => setViewingConservatoryGallery('externalElevationImages'), 'External Elevation')}
                  </div>

                  <div className={photoCaptureContainerClass}>
                    <label className={labelClass}>{requiredMarker}Internal Images</label>
                    <PhotoCapture
                      onPhotosCaptured={(p) => handleConservatoryPhotosCaptured('internalImages', p)}
                      maxPhotos={999}
                      buttonLabel="Capture Internal Images"
                      buttonClass={photoButtonClass}
                    />
                    {renderMultiplePhotoThumbnails(conservatoryPhotos.internalImages, () => setViewingConservatoryGallery('internalImages'), 'Internal Image')}
                  </div>
                </div>
              )}
            </div>
          </fieldset>

          <div className={fieldWrapperClass}>
            <label htmlFor="numberOfExtensions" className={labelClass}>{requiredMarker}Number of extensions (excluding conservatory)</label>
            <select 
              id="numberOfExtensions" 
              name="numberOfExtensions" 
              value={numberOfExtensions} 
              onChange={(e) => {
                const newValue = e.target.value;
                const oldCount = parseInt(numberOfExtensions) || 0;
                const newCount = parseInt(newValue) || 0;
                
                setNumberOfExtensions(newValue);
                
                // 🔧 CRITICAL: Clean up when number is reduced
                if (onDataUpdate) {
                  const isReducing = newCount < oldCount;
                  
                  if (isReducing) {
                    console.log(`🗑️ [ExtensionsForm] Reducing extensions from ${oldCount} to ${newCount}`);
                    
                    // Get the extension types that will be removed
                    const removedExtensions = extensions.slice(newCount);
                    const removedTypes = removedExtensions.map((ext, idx) => 
                      (ext.type || `EXT ${newCount + idx + 1}`).toLowerCase()
                    );
                    
                    console.log(`🗑️ [ExtensionsForm] Removing extensions:`, removedTypes);
                  }
                  
                  onDataUpdate(prev => {
                    // 1. Trim existingExtensions array to prevent zombie data
                    const currentExtensions = prev.extensions?.existingExtensions || [];
                    const trimmedExtensions = currentExtensions.slice(0, newCount);
                    
                    console.log(`🗑️ [ExtensionsForm] Trimming existingExtensions: ${currentExtensions.length} → ${trimmedExtensions.length}`);
                    
                    // 2. Clean up siteNotesData.floors if reducing
                    let updatedFloorPlan = prev.floor_plan;
                    
                    if (isReducing) {
                      const linkedFloorPlanData = prev.floor_plan?.linkedFloorPlanData;
                      let updatedSiteNotesData = linkedFloorPlanData?.siteNotesData;
                      
                      if (updatedSiteNotesData?.floors) {
                        const originalCount = updatedSiteNotesData.floors.length;
                        
                        // Remove extension floors beyond the new count
                        updatedSiteNotesData = {
                          ...updatedSiteNotesData,
                          floors: updatedSiteNotesData.floors.filter((f: any) => {
                            const buildingType = (f.buildingType || '').toLowerCase();
                            const isExtension = buildingType.includes('ext') || buildingType === 'extension';
                            
                            if (!isExtension) return true; // Keep non-extension floors
                            
                            // Parse extension number (e.g., "EXT 1" -> 1)
                            const extNumberMatch = buildingType.match(/ext\s*(\d+)/i);
                            if (extNumberMatch) {
                              const extNumber = parseInt(extNumberMatch[1]);
                              const shouldKeep = extNumber <= newCount;
                              
                              if (!shouldKeep) {
                                console.log(`🗑️ [ExtensionsForm] Removing floor:`, {
                                  buildingType: f.buildingType,
                                  extNumber
                                });
                              }
                              
                              return shouldKeep;
                            }
                            
                            return true; // Keep if we can't parse the number
                          })
                        };
                        
                        console.log(`✅ [ExtensionsForm] Cleaned siteNotesData.floors: ${originalCount} → ${updatedSiteNotesData.floors.length}`);
                      }
                      
                      updatedFloorPlan = {
                        ...prev.floor_plan,
                        linkedFloorPlanData: {
                          ...linkedFloorPlanData,
                          siteNotesData: updatedSiteNotesData
                        }
                      };
                    }
                    
                    return {
                      ...prev,
                      floor_plan: updatedFloorPlan,
                      extensions: {
                        ...prev.extensions,
                        numberOfExtensions: newValue,
                        existingExtensions: trimmedExtensions
                      }
                    };
                  }, 'extensions');
                }
              }} 
              className={commonSelectClassName} 
              required
            >
                {numberOptions(5, "Select number of extensions").map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>

          {extensions.map((ext, index) => (
            <fieldset key={ext.id} className="border border-gray-300 p-4 rounded-lg">
                <legend className="text-md font-semibold text-gray-700 px-2">{ext.type || `Extension ${index + 1}`} Details</legend>
                <div className="space-y-4 mt-2">
                    {/* Display area, HLP, PW, and Height */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {ext.area && (
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                          <div className="text-xs font-semibold text-blue-900 mb-1">Area</div>
                          <div className="text-sm text-gray-600 mb-1">(from Floor Plan)</div>
                          <div className="text-lg font-bold text-blue-700">{ext.area} m²</div>
                        </div>
                      )}
                      {ext.hlp && (
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                          <div className="text-xs font-semibold text-green-900 mb-1">HLP</div>
                          <div className="text-sm text-gray-600 mb-1">(Heat Loss Perimeter)</div>
                          <div className="text-lg font-bold text-green-700">{ext.hlp} m</div>
                        </div>
                      )}
                      {ext.pw && (
                        <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3">
                          <div className="text-xs font-semibold text-amber-900 mb-1">PW</div>
                          <div className="text-sm text-gray-600 mb-1">(Party Wall)</div>
                          <div className="text-lg font-bold text-amber-700">{ext.pw} m</div>
                        </div>
                      )}
                      <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-3">
                        <div className="text-xs font-semibold text-purple-900 mb-1">Height (m)</div>
                        <input
                          type="number"
                          step="0.01"
                          value={ext.height || ''}
                          onChange={(e) => handleExtensionChange(index, 'height', e.target.value)}
                          className="w-full text-lg font-bold text-purple-700 bg-transparent border-0 focus:ring-2 focus:ring-purple-400 rounded p-1"
                          placeholder="2.40"
                        />
                      </div>
                    </div>
                    
                    <div className={fieldWrapperClass}>
                        <label htmlFor={`${ext.id}-numberOfStoreys`} className={labelClass}>{requiredMarker}Number of storeys in {ext.type || `Extension ${index + 1}`}</label>
                        <select id={`${ext.id}-numberOfStoreys`} value={ext.numberOfStoreys} onChange={(e) => handleExtensionChange(index, 'numberOfStoreys', e.target.value)} className={commonSelectClassName} required>
                            {numberOptions(3, 'Select number of storeys').map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                    </div>

                    <div className={fieldWrapperClass}>
                        <label htmlFor={`${ext.id}-buildingAge`} className={labelClass}>{requiredMarker}Building Age of {ext.type || `Extension ${index + 1}`}</label>
                        <select id={`${ext.id}-buildingAge`} value={ext.buildingAge} onChange={(e) => handleExtensionChange(index, 'buildingAge', e.target.value)} className={commonSelectClassName} required>
                            {buildingAgeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                    </div>
                    
                    <div className={fieldWrapperClass}>
                        <label htmlFor={`${ext.id}-wallConstructionType`} className={labelClass}>{requiredMarker}{ext.type || `Extension ${index + 1}`} wall construction type</label>
                        <select id={`${ext.id}-wallConstructionType`} value={ext.wallConstructionType} onChange={(e) => handleExtensionChange(index, 'wallConstructionType', e.target.value)} className={commonSelectClassName} required>
                            {wallConstructionTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                    </div>

                    <div className={photoCaptureContainerClass}>
                        <label className={labelClass}>{requiredMarker}{ext.type || `Extension ${index + 1}`} Wall evidence</label>
                        <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured(index, 'wallEvidence', p)} maxPhotos={999} buttonLabel={`Capture Wall Evidence for Ext ${index + 1}`} buttonClass={photoButtonClass} />
                        {ext.photos && renderMultiplePhotoThumbnails(ext.photos.wallEvidence, () => setViewingGallery({ extIndex: index, photoType: 'wallEvidence' }), `Ext ${index+1} Wall Evidence`)}
                    </div>

                    <div className={photoCaptureContainerClass}>
                        <label className={labelClass}>{requiredMarker}{ext.type || `Extension ${index + 1}`} Wall Thickness</label>
                        <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured(index, 'wallThickness', p)} maxPhotos={999} buttonLabel={`Capture Wall Thickness for Ext ${index + 1}`} buttonClass={photoButtonClass} />
                        {ext.photos && renderMultiplePhotoThumbnails(ext.photos.wallThickness || [], () => setViewingGallery({ extIndex: index, photoType: 'wallThickness' }), `Ext ${index+1} Wall Thickness`)}
                    </div>
                    
                    <div className={fieldWrapperClass}>
                        <label className={labelClass}>{requiredMarker}Wall Thickness Measurement (mm)</label>
                        <input type="text" inputMode="numeric" value={ext.wallThicknessMeasurement || ''} onChange={(e) => handleExtensionChange(index, 'wallThicknessMeasurement', e.target.value)} className={commonSelectClassName} placeholder="Enter thickness in mm" required />
                    </div>
                    
                    <div className={fieldWrapperClass}>
                        <label className={labelClass}>{requiredMarker}Is wall insulated</label>
                        <div className="flex space-x-4 mt-2">
                            <button type="button" onClick={() => handleExtensionChange(index, 'isWallInsulated', true)} className={booleanButtonClass(ext.isWallInsulated ?? null, true)}>Yes</button>
                            <button type="button" onClick={() => handleExtensionChange(index, 'isWallInsulated', false)} className={booleanButtonClass(ext.isWallInsulated ?? null, false)}>No</button>
                        </div>
                    </div>

                    {ext.isWallInsulated === true && (
                        <div className={photoCaptureContainerClass}>
                            <label className={labelClass}>{requiredMarker}Wall Insulation Evidence <span className="text-xs text-gray-500">(Min. 3 Images)</span></label>
                            <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured(index, 'wallInsulationImages', p)} maxPhotos={999} buttonLabel={`Capture Wall Insulation for Ext ${index + 1}`} buttonClass={photoButtonClass} />
                            {ext.photos && renderMultiplePhotoThumbnails(ext.photos.wallInsulationImages, () => setViewingGallery({ extIndex: index, photoType: 'wallInsulationImages' }), `Ext ${index+1} Wall Insulation`)}
                            {ext.photos && (!ext.photos.wallInsulationImages || ext.photos.wallInsulationImages.length < 3) && (
                                <p className="text-xs text-red-500 mt-1">Minimum 3 images required.</p>
                            )}
                        </div>
                    )}

                    <div className={fieldWrapperClass}>
                        <label className={labelClass}>{requiredMarker}Roof Type {ext.type || `Extension ${index + 1}`}</label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 mt-2">
                            {roofTypeOptions.map(opt => (
                                <label key={opt.value} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                                    <input type="radio" name={`${ext.id}-roofType`} value={opt.value} checked={ext.roofType === opt.value} onChange={(e) => handleExtensionChange(index, 'roofType', e.target.value)} className="form-radio text-yellow-500 focus:ring-yellow-400"/>
                                    <span className="text-sm text-gray-700">{opt.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className={photoCaptureContainerClass}>
                        <label className={labelClass}>{requiredMarker}Roof type {ext.type || `Ext ${index + 1}`} Evidence</label>
                        <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured(index, 'roofTypeEvidence', p)} maxPhotos={999} buttonLabel={`Capture Roof Evidence for Ext ${index + 1}`} buttonClass={photoButtonClass} />
                        {ext.photos && renderMultiplePhotoThumbnails(ext.photos.roofTypeEvidence, () => setViewingGallery({ extIndex: index, photoType: 'roofTypeEvidence' }), `Ext ${index+1} Roof Evidence`)}
                    </div>

                    <div className={photoCaptureContainerClass}>
                        <label className={labelClass}>{requiredMarker}{ext.type || `Extension ${index + 1}`} External Elevations</label>
                        <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured(index, 'externalElevations', p)} maxPhotos={999} buttonLabel={`Capture External Elevations for Ext ${index + 1}`} buttonClass={photoButtonClass} />
                        {ext.photos && renderMultiplePhotoThumbnails(ext.photos.externalElevations, () => setViewingGallery({ extIndex: index, photoType: 'externalElevations' }), `Ext ${index+1} External Elevations`)}
                    </div>

                </div>
            </fieldset>
          ))}
        </div>

        <div className="mt-auto pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:justify-between">
          <button type="button" onClick={onNavigateBack} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]">
            <ArrowLeft size={18} /> Previous
          </button>
          <button type="submit" className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]">
            Next <ArrowRight size={18} />
          </button>
        </div>
      </form>

      {/* Photo Gallery Modal for Extension Photos */}
      {viewingGallery && extensions[viewingGallery.extIndex]?.photos && (
        <PhotoGalleryModal
          isOpen={true}
          onClose={() => setViewingGallery(null)}
          photos={extensions[viewingGallery.extIndex].photos![viewingGallery.photoType].map(p => ({
            id: p.displayId,
            fileName: p.fileName,
            url: p.url,
            localPath: p.dataUrl,
            uploading: p.uploading,
            error: p.error,
            note: p.note || '',
            caption: p.caption,
            timestamp: p.timestamp,
          }))}
          sectionTitle={`${extensions[viewingGallery.extIndex].type || `Extension ${viewingGallery.extIndex + 1}`} - ${viewingGallery.photoType.replace(/([A-Z])/g, ' $1').trim()}`}
          onDeletePhoto={(photoId) => removePhoto(viewingGallery.extIndex, viewingGallery.photoType, photoId)}
          onUpdateNote={(photoId, note) => {
            // Update note in the photo
            setExtensions(prev => {
              const updated = [...prev];
              const extPhotos = updated[viewingGallery.extIndex].photos;
              if (extPhotos) {
                const photoIndex = extPhotos[viewingGallery.photoType].findIndex(p => p.displayId === photoId);
                if (photoIndex !== -1) {
                  extPhotos[viewingGallery.photoType][photoIndex].note = note;
                }
              }
              
              // Persist to surveyData
              if (onDataUpdate) {
                onDataUpdate(prevData => ({
                  ...prevData,
                  extensions: {
                    ...prevData.extensions,
                    existingExtensions: updated
                  }
                }), 'extensions');
              }
              
              return updated;
            });
          }}
        />
      )}

      {/* Photo Gallery Modal for Conservatory Photos */}
      {viewingConservatoryGallery && (
        <PhotoGalleryModal
          isOpen={true}
          onClose={() => setViewingConservatoryGallery(null)}
          photos={conservatoryPhotos[viewingConservatoryGallery].map(p => ({
            id: p.displayId,
            fileName: p.fileName,
            url: p.url,
            localPath: p.dataUrl,
            uploading: p.uploading,
            error: p.error,
            note: p.note || '',
            caption: p.caption,
            timestamp: p.timestamp,
          }))}
          sectionTitle={`Conservatory - ${viewingConservatoryGallery.replace(/([A-Z])/g, ' $1').trim()}`}
          onDeletePhoto={(photoId) => removeConservatoryPhoto(viewingConservatoryGallery, photoId)}
          onUpdateNote={(photoId, note) => {
            // Update note in the photo
            setConservatoryPhotos(prev => {
              const updated = {
                ...prev,
                [viewingConservatoryGallery]: prev[viewingConservatoryGallery].map(p => 
                  p.displayId === photoId ? { ...p, note } : p
                )
              };
              
              // Persist to surveyData
              if (onDataUpdate) {
                onDataUpdate(prevData => ({
                  ...prevData,
                  extensions: {
                    ...prevData.extensions,
                    conservatoryPhotos: updated
                  }
                }), 'extensions');
              }
              
              return updated;
            });
          }}
        />
      )}
    </div>
  );
};

export default ExtensionsForm; 